import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { rememberReturnTo } from '../../utils/returnTo';
import relayIcon from '../../assets/icons/relay-icon.png';
import '../../types/webview';

interface SignInScreenProps {
  onSignIn: () => void;
}

const SignInScreen: React.FC<SignInScreenProps> = ({ onSignIn }) => {
  const [email, setEmail] = useState('');
  const { loginWithRedirect } = useAuth0();

  // Listen for Google Sign-In results from Android
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'google-signin-success') {
        // Handle successful Google Sign-In
        console.log('Google Sign-In successful:', event.data.data);
        // You can process the user data here and call onSignIn()
        onSignIn();
      } else if (event.data?.type === 'google-signin-error') {
        // Handle Google Sign-In error
        console.error('Google Sign-In failed:', event.data.error);
        // You can show an error message to the user
      }
    };

    window.addEventListener('message', handleMessage);

    // Test the connection when component mounts
    if (
      window.ReactNativeWebView &&
      typeof window.ReactNativeWebView.testConnection === 'function'
    ) {
      console.log('Testing WebView connection...');
      window.ReactNativeWebView.testConnection();
    }

    return () => window.removeEventListener('message', handleMessage);
  }, [onSignIn]);

  const handleGoogleSignIn = () => {
    // Every way out of this screen leaves the app, so the page the driver
    // opened is saved before any of them.
    rememberReturnTo();
    console.log('Google Sign-In button clicked');
    console.log('window.ReactNativeWebView:', window.ReactNativeWebView);
    console.log(
      'Available methods:',
      window.ReactNativeWebView
        ? Object.keys(window.ReactNativeWebView)
        : 'Not available'
    );

    // Check if we're in embedded mode (Android WebView)
    if (
      window.ReactNativeWebView &&
      typeof window.ReactNativeWebView.signInWithGoogle === 'function'
    ) {
      console.log('Using native Google Sign-In for Android');
      // Use native Google Sign-In for Android
      window.ReactNativeWebView.signInWithGoogle();
    } else {
      console.log('Using web-based Auth0 for regular browsers');
      // Use web-based Auth0 for regular browsers
      loginWithRedirect({
        authorizationParams: {
          connection: 'google-oauth2',
        },
      });
    }
  };

  const handleEmailContinue = () => {
    rememberReturnTo();
    if (email.trim()) {
      loginWithRedirect({
        authorizationParams: {
          login_hint: email,
        },
      });
    }
  };

  const handleSignUp = () => {
    rememberReturnTo();
    console.log('Sign up clicked');
    // Navigate to sign up screen
  };

  return (
    <div className="fixed inset-0 bg-primary flex flex-col items-center justify-center p-4">
      {/* Main Sign-In Card */}
      <div className="bg-background rounded-3xl p-6 w-full max-w-sm mx-auto shadow-lg">
        {/* Logo Section */}
        <div className="flex items-center mb-6">
          <div className="w-8 h-8 flex items-center justify-center mr-3">
            <img
              src={relayIcon}
              alt="Relay Towing"
              className="w-full h-full object-contain"
            />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">
              Sign in to Relay Tow
            </h1>
            <p className="text-sm text-gray-600">
              Welcome back! Please sign in to continue
            </p>
          </div>
        </div>

        {/* Google Sign-In Button */}
        <button
          onClick={handleGoogleSignIn}
          className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors mb-4"
        >
          <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          <span className="text-gray-700 font-medium">
            Continue with Google
          </span>
        </button>

        {/* Divider */}
        <div className="flex items-center my-4">
          <div className="flex-1 border-t border-gray-300"></div>
          <span className="px-3 text-sm text-gray-500">or</span>
          <div className="flex-1 border-t border-gray-300"></div>
        </div>

        {/* Email Input */}
        <div className="mb-4">
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Email Address
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Enter your email"
          />
        </div>

        {/* Continue Button */}
        <button
          onClick={handleEmailContinue}
          className="w-full bg-black text-white py-3 rounded-lg font-medium hover:bg-gray-900 transition-colors mb-4"
        >
          Continue
        </button>

        {/* Sign Up Link */}
        <div className="text-center">
          <span className="text-sm text-gray-600">Don't have an account? </span>
          <button
            onClick={handleSignUp}
            className="text-sm font-medium text-gray-900 hover:underline"
          >
            Sign up
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center">
        <p className="text-white text-sm mb-4">Version 1.0</p>
        <div className="flex items-center justify-center space-x-4">
          <button className="text-white text-sm hover:underline">
            Terms and conditions
          </button>
          <span className="text-white text-sm">&</span>
          <button className="text-white text-sm hover:underline">FAQ</button>
        </div>
      </div>
    </div>
  );
};

export default SignInScreen;
